import { cn } from '../lib/cn';

export interface SmolitMarkProps {
  /** Rendered size in px. The export uses 24 in-line, 40 on the FAB, 54 in heroes. */
  size?: number;
  /**
   * `moana` is the default teal body; `white` inverts it for placement on a
   * saturated moana surface such as the floating action button.
   */
  tone?: 'moana' | 'white';
  /** Blinking is on by default; the reduced-motion query stops it globally. */
  blink?: boolean;
  className?: string;
  title?: string;
}

/**
 * Smolit's avatar: a rounded teal body with two eyes, a smile, and a
 * pōhutukawa spark at the shoulder. Reproduced from the export's
 * `smolitMarkEl` renderer, including the 5.5s blink.
 */
export function SmolitMark({
  size = 24,
  tone = 'moana',
  blink = true,
  className,
  title = 'Smolit',
}: SmolitMarkProps) {
  const white = tone === 'white';
  const body = white ? '#FFFFFF' : '#2BA6A4';
  const eye = white ? '#0E6B69' : '#FFFFFF';
  const pupil = white ? '#2BA6A4' : '#0D3B3A';
  const mouth = white ? '#0E6B69' : '#FFFFFF';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      className={cn('block', className)}
      role="img"
      aria-label={title}
    >
      <rect x={3} y={4} width={34} height={32} rx={11} fill={body} />
      <g
        style={
          blink
            ? { transformOrigin: '20px 19px', animation: 'orBlink 5.5s infinite' }
            : { transformOrigin: '20px 19px' }
        }
      >
        <circle cx={15} cy={18} r={3.2} fill={eye} />
        <circle cx={25} cy={18} r={3.2} fill={eye} />
        <circle cx={15.8} cy={18.6} r={1.4} fill={pupil} />
        <circle cx={25.8} cy={18.6} r={1.4} fill={pupil} />
      </g>
      <path d="M15 26 q5 4 10 0" stroke={mouth} strokeWidth={2} fill="none" strokeLinecap="round" />
      <path d="M32 5 l1.4 3 3 1.4 -3 1.4 -1.4 3 -1.4 -3 -3 -1.4 3 -1.4Z" fill="#FF6B4A" />
    </svg>
  );
}

export interface SmolitFabProps {
  onClick: () => void;
  className?: string;
}

/** The pulsing moana action button that opens Smolit from any screen. */
export function SmolitFab({ onClick, className }: SmolitFabProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Open Smolit"
      className={cn(
        'fixed right-[26px] bottom-[26px] z-50 flex h-[60px] w-[60px] cursor-pointer items-center',
        'justify-center rounded-full border-none bg-moana p-0 shadow-fab',
        'transition-transform duration-fast ease-standard hover:scale-[1.06]',
        className,
      )}
    >
      <span className="absolute inset-0 animate-ring rounded-full border-2 border-moana" />
      <SmolitMark size={40} tone="white" />
    </button>
  );
}
